/**
 * Fixed starting dates months filter.
 *
 * Year tabs swap the visible month grid and drop whatever was picked in the year being
 * left. Picking and unpicking months is left to the browser -- they are plain checkboxes
 * -- and the archive filters off the change event they fire.
 */

const ROW = '.wte-fsd-months__years-wrap';
const LIST = '.wte-fsd-months__years';
const TAB = '.wte-fsd-months__year';

const ARROW_WIDTH = 32;
const EDGE = 2;

const openYear = ( wrapper, year ) => {
	wrapper.querySelectorAll( TAB ).forEach( ( tab ) => {
		const isActive = tab.dataset.year === year;
		tab.classList.toggle( 'is-active', isActive );
		tab.setAttribute( 'aria-selected', isActive ? 'true' : 'false' );
	} );

	wrapper.querySelectorAll( '.wte-fsd-months__panel' ).forEach( ( panel ) => {
		const isOpen = panel.dataset.year === year;
		panel.classList.toggle( 'is-active', isOpen );
		panel.hidden = ! isOpen;
	} );
};

/**
 * Points a row at the picked year and shows the arrows that still lead somewhere.
 *
 * A picked year further along the row would sit off screen, or half under an arrow, so
 * the row scrolls by just enough to clear it -- but only when asked, a sync alone must
 * not yank the row away from wherever it was scrolled to.
 *
 * @param {Element} row      The row to measure.
 * @param {boolean} reveal   Whether to scroll the picked year into the clear.
 */
const syncRow = ( row, reveal = false ) => {
	const list = row.querySelector( LIST );
	const overflow = list.scrollWidth - list.clientWidth;
	const active = reveal ? list.querySelector( `${ TAB }.is-active` ) : null;

	if ( active ) {
		const left = active.offsetLeft - list.scrollLeft;
		const right = left + active.offsetWidth;
		const lastVisible = list.clientWidth - ( overflow > 0 ? ARROW_WIDTH : 0 );

		if ( left < ARROW_WIDTH ) {
			list.scrollLeft -= ARROW_WIDTH - left;
		} else if ( right > lastVisible ) {
			list.scrollLeft += right - lastVisible;
		}
	}

	row.querySelector( '.wte-fsd-months__scroll--prev' ).hidden = list.scrollLeft <= EDGE;
	row.querySelector( '.wte-fsd-months__scroll--next' ).hidden = list.scrollLeft >= overflow - EDGE;
};

// Scroll and resize fire in bursts, and every sync measures the row, so they are batched
// into the frame that paints them.
let frame = 0;

const syncRows = ( reveal = false ) => {
	cancelAnimationFrame( frame );

	frame = requestAnimationFrame( () => document.querySelectorAll( ROW ).forEach( ( row ) => syncRow( row, reveal ) ) );
};

const clearPicks = ( wrapper ) => {
	const picked = wrapper.querySelectorAll( 'input[type="checkbox"]:checked' );

	picked.forEach( ( input ) => {
		input.checked = false;
	} );

	return picked;
};

const notifyChange = ( input ) => input.dispatchEvent( new Event( 'change', { bubbles: true } ) );

document.addEventListener( 'click', ( event ) => {
	const arrow = event.target.closest?.( '.wte-fsd-months__scroll' );

	if ( arrow ) {
		const list = arrow.closest( ROW ).querySelector( LIST );
		const step = list.clientWidth * 0.75;

		list.scrollBy( { left: arrow.matches( '.wte-fsd-months__scroll--prev' ) ? -step : step } );

		list.addEventListener( 'scrollend', () => syncRows(), { once: true } );
		setTimeout( () => syncRows(), 400 );

		return;
	}

	// "Clear all" wipes the picks itself. The grid goes back to the first year, scrolled
	// back to it, then loses the active mark -- nothing is picked, so no tab reads as
	// selected.
	if ( event.target.closest( '#reset-trip-search-criteria' ) ) {
		document.querySelectorAll( '.wte-fsd-months' ).forEach( ( wrapper ) => {
			clearPicks( wrapper );

			const tabs = wrapper.querySelectorAll( TAB );

			openYear( wrapper, tabs[ 0 ]?.dataset.year );

			tabs.forEach( ( tab ) => {
				tab.classList.remove( 'is-active' );
				tab.setAttribute( 'aria-selected', 'false' );
			} );

			wrapper.querySelector( LIST ).scrollLeft = 0;
		} );

		syncRows();

		return;
	}

	const tab = event.target.closest( TAB );

	if ( ! tab ) {
		return;
	}

	event.preventDefault();

	const wrapper = tab.closest( '.wte-fsd-months' );

	openYear( wrapper, tab.dataset.year );

	// Picking a tab half hidden under an arrow should bring it out from under it.
	syncRow( tab.closest( ROW ), true );

	// A pick of the year being left would keep filtering from a grid nobody can see, and
	// the year opened becomes the pick until a month of it is picked instead.
	clearPicks( wrapper );

	const year = wrapper.querySelector( `.wte-fsd-months__panel[data-year="${ tab.dataset.year }"] .wte-fsd-months__year-input` );

	if ( year ) {
		year.checked = true;
		notifyChange( year );
	}
} );

// A month is the narrower pick, so it replaces the year it belongs to. Caught on the way
// down, before the archive collects what is checked.
document.addEventListener( 'change', ( event ) => {
	const month = event.target.closest?.( '.wte-fsd-months__month' );

	if ( ! month || ! event.target.checked ) {
		return;
	}

	const wrapper = month.closest( '.wte-fsd-months' );

	wrapper.querySelectorAll( '.wte-fsd-months__year-input:checked' ).forEach( ( input ) => {
		input.checked = false;
	} );

	openYear( wrapper, month.closest( '.wte-fsd-months__panel' ).dataset.year );
	syncRow( wrapper.querySelector( ROW ), true );
}, true );

// Scroll does not bubble, so the row is caught on the way down.
document.addEventListener( 'scroll', ( event ) => {
	if ( event.target.classList?.contains( 'wte-fsd-months__years' ) ) {
		syncRows();
	}
}, true );

window.addEventListener( 'resize', () => syncRows() );
// The sidebar can be collapsed on load, which leaves every row measuring zero.
document.addEventListener( 'tripfetched', () => syncRows() );
window.addEventListener( 'load', () => syncRows( true ) );

syncRows( true );
